import { useCallback, useEffect } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
  alt?: boolean;
  handler: (event: KeyboardEvent) => void;
  description?: string;
  preventDefault?: boolean;
}

interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
  enableWhenInputFocused?: boolean;
}

const isInputElement = (element: Element): boolean => {
  const tagName = element.tagName.toLowerCase();
  const inputTypes = ['input', 'textarea', 'select'];

  if (inputTypes.includes(tagName)) {
    return true;
  }

  // Check for contenteditable
  if (element.getAttribute('contenteditable') === 'true') {
    return true;
  }

  // Check for Monaco Editor
  if (element.closest('.monaco-editor')) {
    return true;
  }

  return false;
};

const matchesShortcut = (event: KeyboardEvent, shortcut: KeyboardShortcut): boolean => {
  const { key, ctrl = false, meta = false, shift = false, alt = false } = shortcut;

  const keyMatches = event.key.toLowerCase() === key.toLowerCase();
  const ctrlMatches = event.ctrlKey === ctrl;
  const metaMatches = event.metaKey === meta;
  const shiftMatches = event.shiftKey === shift;
  const altMatches = event.altKey === alt;

  return keyMatches && ctrlMatches && metaMatches && shiftMatches && altMatches;
};

export const useKeyboardShortcuts = ({
  shortcuts,
  enabled = true,
  enableWhenInputFocused = false,
}: UseKeyboardShortcutsOptions) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Skip if typing in an input and not explicitly enabled
      if (!enableWhenInputFocused && isInputElement(event.target as Element)) {
        return;
      }

      // Find matching shortcut
      const matchingShortcut = shortcuts.find((shortcut) =>
        matchesShortcut(event, shortcut)
      );

      if (matchingShortcut) {
        if (matchingShortcut.preventDefault !== false) {
          event.preventDefault();
          event.stopPropagation();
        }
        matchingShortcut.handler(event);
      }
    },
    [shortcuts, enabled, enableWhenInputFocused]
  );

  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown, { capture: true });
      return () => {
        document.removeEventListener('keydown', handleKeyDown, { capture: true });
      };
    }
  }, [handleKeyDown, enabled]);
};

// Helper for common modifier combinations
export const createShortcut = (
  key: string,
  handler: (event: KeyboardEvent) => void,
  options: Partial<Omit<KeyboardShortcut, 'key' | 'handler'>> = {}
): KeyboardShortcut => ({
  key,
  handler,
  ...options,
});

// Common keyboard shortcuts factory
export const shortcuts = {
  save: (handler: () => void) => createShortcut('s', handler, {
    ctrl: true,
    meta: true,
    description: 'Save current item'
  }),

  new: (handler: () => void) => createShortcut('n', handler, {
    ctrl: true,
    meta: true,
    description: 'Create new item'
  }),

  escape: (handler: () => void) => createShortcut('Escape', handler, {
    description: 'Close modal or clear selection'
  }),

  search: (handler: () => void) => createShortcut('k', handler, {
    ctrl: true,
    meta: true,
    description: 'Focus search'
  }),

  toggleView: (handler: () => void) => createShortcut('e', handler, {
    ctrl: true,
    meta: true,
    description: 'Toggle view mode'
  }),

  delete: (handler: () => void) => createShortcut('Delete', handler, {
    description: 'Delete selected item'
  }),

  enter: (handler: () => void) => createShortcut('Enter', handler, {
    description: 'Confirm or open selected item'
  }),
}; 